// Types
export type Direction = 'ltr' | 'rtl';
export type Alignment = 'left' | 'right' | 'start' | 'end' | 'center';
export type LogicalSide = 'start' | 'end';
export type PhysicalSide = 'left' | 'right';

// RTL language codes
const RTL_LOCALES = ['ar', 'he', 'fa', 'ur', 'yi', 'ji', 'iw', 'ku', 'ps', 'sd', 'ug'];

// Simple RTL detection function to avoid external library issues during SSR
function getLanguageDirection(locale: string): Direction {
  const language = locale.split('-')[0].toLowerCase();
  return RTL_LOCALES.includes(language) ? 'rtl' : 'ltr';
}

/**
 * Check if a locale is RTL
 */
export function isRTLLocale(locale: string): boolean {
  return getLanguageDirection(locale) === 'rtl';
}

/**
 * Get text direction for a locale
 */
export function getDirection(locale: string): Direction {
  return getLanguageDirection(locale);
}

/**
 * Flip alignment based on direction
 */
export function flipAlignment(align: Alignment, isRTL: boolean): Alignment {
  if (!isRTL) return align;

  switch (align) {
    case 'left':
      return 'right';
    case 'right':
      return 'left';
    case 'start':
    case 'end':
    case 'center':
      return align;
    default:
      return align;
  }
}

/**
 * Convert physical side to logical side
 */
export function toLogicalSide(side: PhysicalSide, isRTL: boolean): LogicalSide {
  if (isRTL) {
    return side === 'left' ? 'end' : 'start';
  }
  return side === 'left' ? 'start' : 'end';
}

/**
 * Convert logical side to physical side
 */
export function toPhysicalSide(side: LogicalSide, isRTL: boolean): PhysicalSide {
  if (isRTL) {
    return side === 'start' ? 'right' : 'left';
  }
  return side === 'start' ? 'left' : 'right';
}

/**
 * CSS property mapping for RTL
 */
export const logicalProperties = {
  // Margin
  marginLeft: 'marginInlineStart',
  marginRight: 'marginInlineEnd',
  marginTop: 'marginBlockStart',
  marginBottom: 'marginBlockEnd',

  // Padding
  paddingLeft: 'paddingInlineStart',
  paddingRight: 'paddingInlineEnd',
  paddingTop: 'paddingBlockStart',
  paddingBottom: 'paddingBlockEnd',

  // Border
  borderLeft: 'borderInlineStart',
  borderRight: 'borderInlineEnd',
  borderTop: 'borderBlockStart',
  borderBottom: 'borderBlockEnd',
  borderLeftWidth: 'borderInlineStartWidth',
  borderRightWidth: 'borderInlineEndWidth',
  borderLeftColor: 'borderInlineStartColor',
  borderRightColor: 'borderInlineEndColor',
  borderLeftStyle: 'borderInlineStartStyle',
  borderRightStyle: 'borderInlineEndStyle',

  // Position
  left: 'insetInlineStart',
  right: 'insetInlineEnd',
  top: 'insetBlockStart',
  bottom: 'insetBlockEnd',

  // Size
  width: 'inlineSize',
  height: 'blockSize',
  minWidth: 'minInlineSize',
  minHeight: 'minBlockSize',
  maxWidth: 'maxInlineSize',
  maxHeight: 'maxBlockSize',

  // Text
  textAlign: {
    left: 'start',
    right: 'end',
  },

  // Float
  float: {
    left: 'inline-start',
    right: 'inline-end',
  },
} as const;

/**
 * Convert physical CSS properties to logical properties
 */
export function toLogicalProperty(
  property: string,
  value?: string
): { property: string; value?: string } {
  // Handle text-align and float specially
  if (
    property === 'textAlign' &&
    value &&
    logicalProperties.textAlign[value as keyof typeof logicalProperties.textAlign]
  ) {
    return {
      property,
      value: logicalProperties.textAlign[value as keyof typeof logicalProperties.textAlign],
    };
  }

  if (
    property === 'float' &&
    value &&
    logicalProperties.float[value as keyof typeof logicalProperties.float]
  ) {
    return {
      property,
      value: logicalProperties.float[value as keyof typeof logicalProperties.float],
    };
  }

  // Handle other properties
  const logicalProp = logicalProperties[property as keyof typeof logicalProperties];
  if (typeof logicalProp === 'string') {
    return { property: logicalProp, value };
  }

  return { property, value };
}

/**
 * Transform style object to use logical properties
 */
export function toLogicalStyles(styles: React.CSSProperties): React.CSSProperties {
  const logicalStyles: React.CSSProperties = {};

  for (const [key, value] of Object.entries(styles)) {
    const { property, value: transformedValue } = toLogicalProperty(key, value?.toString());
    logicalStyles[property as keyof React.CSSProperties] = transformedValue || value;
  }

  return logicalStyles;
}

/**
 * Get directional icon based on RTL
 */
export function getDirectionalIcon(ltrIcon: string, rtlIcon: string, isRTL: boolean): string {
  return isRTL ? rtlIcon : ltrIcon;
}

/**
 * Check if an icon should flip in RTL
 */
export function shouldFlipIcon(iconName: string): boolean {
  // Icons that should flip
  const flippableIcons = [
    'arrow',
    'chevron',
    'caret',
    'back',
    'forward',
    'next',
    'previous',
    'left',
    'right',
    'reply',
    'undo',
    'redo',
    'quote',
    'list',
    'indent',
    'outdent',
    'align',
    'paragraph',
    'text',
    'document',
    'file',
    'folder',
    'breadcrumb',
    'navigation',
    'menu',
    'sidebar',
    'panel',
    'drawer',
    'expand',
    'collapse',
    'resize',
    'move',
    'drag',
    'sort',
    'order',
    'first',
    'last',
    'start',
    'end',
    'beginning',
    'finish',
  ];

  // Icons that should NOT flip
  const nonFlippableIcons = [
    'play',
    'pause',
    'stop',
    'record',
    'rewind',
    'fastforward',
    'check',
    'close',
    'plus',
    'minus',
    'times',
    'cross',
    'clock',
    'time',
    'calendar',
    'date',
    'search',
    'zoom',
    'home',
    'user',
    'settings',
    'gear',
    'cog',
    'info',
    'help',
    'warning',
    'error',
    'success',
    'notification',
    'bell',
    'heart',
    'star',
    'bookmark',
    'flag',
    'tag',
    'label',
    'download',
    'upload',
    'refresh',
    'sync',
    'reload',
    'lock',
    'unlock',
    'key',
    'shield',
    'security',
    'phone',
    'email',
    'message',
    'chat',
    'comment',
    'share',
    'link',
    'chain',
    'attachment',
    'paperclip',
    'print',
    'save',
    'edit',
    'delete',
    'trash',
    'remove',
    'filter',
    'sort',
    'view',
    'grid',
    'list',
    'table',
    'chart',
    'graph',
    'analytics',
    'statistics',
    'report',
    'logo',
    'brand',
    'icon',
    'image',
    'photo',
    'picture',
    'video',
    'audio',
    'music',
    'microphone',
    'camera',
    'location',
    'map',
    'pin',
    'marker',
    'gps',
    'compass',
    'currency',
    'dollar',
    'euro',
    'pound',
    'yen',
    'bitcoin',
    'percent',
    'hashtag',
    'at',
    'ampersand',
    'asterisk',
  ];

  const iconLower = iconName.toLowerCase();

  // Check if explicitly non-flippable
  if (nonFlippableIcons.some((icon) => iconLower.includes(icon))) {
    return false;
  }

  // Check if flippable
  return flippableIcons.some((icon) => iconLower.includes(icon));
}

/**
 * Mirror transform for RTL
 */
export function getMirrorTransform(shouldMirror: boolean): string {
  return shouldMirror ? 'scaleX(-1)' : 'none';
}

/**
 * Get border radius values for directional corners
 */
export function getDirectionalBorderRadius(
  topStart: number | string,
  topEnd: number | string,
  bottomEnd: number | string,
  bottomStart: number | string,
  isRTL: boolean
): {
  borderTopLeftRadius: number | string;
  borderTopRightRadius: number | string;
  borderBottomRightRadius: number | string;
  borderBottomLeftRadius: number | string;
} {
  if (isRTL) {
    return {
      borderTopLeftRadius: topEnd,
      borderTopRightRadius: topStart,
      borderBottomRightRadius: bottomStart,
      borderBottomLeftRadius: bottomEnd,
    };
  }

  return {
    borderTopLeftRadius: topStart,
    borderTopRightRadius: topEnd,
    borderBottomRightRadius: bottomEnd,
    borderBottomLeftRadius: bottomStart,
  };
}

/**
 * Get Chakra UI logical property shortcuts
 */
export const chakraLogicalProps = {
  // Margin shortcuts
  ms: 'marginInlineStart',
  me: 'marginInlineEnd',
  marginStart: 'marginInlineStart',
  marginEnd: 'marginInlineEnd',

  // Padding shortcuts
  ps: 'paddingInlineStart',
  pe: 'paddingInlineEnd',
  paddingStart: 'paddingInlineStart',
  paddingEnd: 'paddingInlineEnd',

  // Position shortcuts
  insetStart: 'insetInlineStart',
  insetEnd: 'insetInlineEnd',
  start: 'insetInlineStart',
  end: 'insetInlineEnd',

  // Border shortcuts
  borderStart: 'borderInlineStart',
  borderEnd: 'borderInlineEnd',
  borderStartWidth: 'borderInlineStartWidth',
  borderEndWidth: 'borderInlineEndWidth',
  borderStartColor: 'borderInlineStartColor',
  borderEndColor: 'borderInlineEndColor',
  borderStartStyle: 'borderInlineStartStyle',
  borderEndStyle: 'borderInlineEndStyle',
} as const;

/**
 * Check if a style property is directional
 */
export function isDirectionalProperty(property: string): boolean {
  return !!(
    logicalProperties[property as keyof typeof logicalProperties] ||
    chakraLogicalProps[property as keyof typeof chakraLogicalProps] ||
    property.includes('left') ||
    property.includes('right') ||
    property.includes('Left') ||
    property.includes('Right') ||
    property === 'textAlign' ||
    property === 'float'
  );
}

/**
 * Format number with locale-specific separators
 */
export function formatNumberForLocale(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Format currency with locale-specific formatting
 */
export function formatCurrencyForLocale(value: number, locale: string, currency: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Get reading direction CSS value
 */
export function getReadingDirection(isRTL: boolean): 'ltr' | 'rtl' {
  return isRTL ? 'rtl' : 'ltr';
}

// Export all utilities
export default {
  isRTLLocale,
  getDirection,
  flipAlignment,
  toLogicalSide,
  toPhysicalSide,
  toLogicalProperty,
  toLogicalStyles,
  getDirectionalIcon,
  shouldFlipIcon,
  getMirrorTransform,
  getDirectionalBorderRadius,
  isDirectionalProperty,
  formatNumberForLocale,
  formatCurrencyForLocale,
  getReadingDirection,
  logicalProperties,
  chakraLogicalProps,
  RTL_LOCALES,
};
